/**
 * Rotate Secret Confirmation
 *
 * Per Design 5.1: Destructive action dialog warning that the current
 * secret will be immediately invalidated.
 */

import { AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Button,
} from '@/components/ui';

interface RotateSecretConfirmationProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  merchantId: string | null;
  onConfirm: () => void;
  isRotating?: boolean;
}

export function RotateSecretConfirmation({
  open,
  onOpenChange,
  merchantId,
  onConfirm,
  isRotating = false,
}: RotateSecretConfirmationProps) {
  const { t } = useTranslation('webhook-config');

  if (!merchantId) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <div className="flex items-center gap-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-yellow-500/10">
              <AlertTriangle className="h-5 w-5 text-warning" />
            </div>
            <div>
              <DialogTitle>{t('secret.rotate.title')}</DialogTitle>
              <DialogDescription>{t('secret.rotate.warning')}</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="py-4">
          <div className="rounded-md border bg-muted/50 p-4">
            <p className="font-mono text-sm text-muted-foreground">{merchantId}</p>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isRotating}>
            {t('common:button.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button variant="destructive" onClick={onConfirm} disabled={isRotating}>
            {isRotating
              ? t('secret.rotate.rotating', { defaultValue: 'Rotating...' })
              : t('secret.rotate.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
